import { useCallback, useEffect, useState } from 'react';
import { Toast } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import {
  h5ManualActivityApi,
  H5ManualTask,
  H5RedemptionAuthMethod,
  H5RedemptionSession,
  H5RedemptionSessionStatus,
} from '../../../api/h5-manual-activity';
import { ManualActivityState } from './types';

const COMPLETED_REDEMPTION_STATUSES: H5RedemptionSessionStatus[] = ['completed', 'destroyed'];
const RETRYABLE_REDEMPTION_STATUSES: H5RedemptionSessionStatus[] = ['failed', 'expired'];

const initialState: ManualActivityState = {
  loading: true,
  actionLoading: false,
  overview: null,
  task: null,
  redemption: null,
  redemptionPhone: '',
  redemptionAuthMethod: 'pairing_code',
  redemptionQrImage: null,
  credentialLoading: false,
  error: '',
  redeemRequired: false,
};

const hasLoginCredential = (
  redemption: H5RedemptionSession | null,
  authMethod: H5RedemptionAuthMethod | null = redemption?.auth_method || null
): boolean => {
  if (!redemption) {
    return false;
  }
  return authMethod === 'qr'
    ? Boolean(redemption.qr_payload)
    : Boolean(redemption.pairing_code);
};

const isCompletedRedemption = (redemption: H5RedemptionSession | null): boolean => (
  Boolean(redemption && COMPLETED_REDEMPTION_STATUSES.includes(redemption.status))
);

const isRetryableRedemption = (redemption: H5RedemptionSession | null): boolean => (
  Boolean(redemption && RETRYABLE_REDEMPTION_STATUSES.includes(redemption.status))
);

const needsAccountScanRetry = (redemption: H5RedemptionSession | null): boolean => (
  Boolean(
    redemption?.wa_account_id
      && (
        isRetryableRedemption(redemption)
        || (redemption.status === 'scanning' && !redemption.wa_connection_attempt_id)
      )
  )
);

const needsLoginCredentialRegeneration = (
  redemption: H5RedemptionSession | null,
  authMethod?: H5RedemptionAuthMethod | null
): boolean => (
  Boolean(
    redemption
      && redemption.status === 'waiting_login'
      && !redemption.wa_connection_attempt_id
      && !redemption.wa_account_id
      && !hasLoginCredential(redemption, authMethod || redemption.auth_method)
  )
);

const isRuntimeBusyMessage = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return normalized.includes('browser is already running') || normalized.includes('userdatadir');
};

export function useManualActivityModel() {
  const { t } = useTranslation('manualActivity');
  const [state, setState] = useState<ManualActivityState>(initialState);

  const applyRedemption = useCallback((redemption: H5RedemptionSession) => {
    setState((previous) => ({
      ...previous,
      redemption,
      redemptionPhone: redemption.whatsapp_phone_number || previous.redemptionPhone,
      redemptionAuthMethod: redemption.auth_method || previous.redemptionAuthMethod,
      credentialLoading: previous.credentialLoading
        && redemption.status === 'waiting_login'
        && !redemption.error_message
        && !hasLoginCredential(redemption, redemption.auth_method || previous.redemptionAuthMethod),
      redeemRequired: !isCompletedRedemption(redemption) || Boolean(previous.overview?.can_redeem),
      overview: previous.overview
        ? {
            ...previous.overview,
            active_redemption: redemption,
          }
        : previous.overview,
    }));
  }, []);

  const applyTask = useCallback((task: H5ManualTask | null) => {
    setState((previous) => {
      const pendingCount = task?.pending_count ?? previous.overview?.pending_count ?? 0;
      const redeemThreshold = task?.redeem_threshold ?? previous.overview?.redeem_threshold ?? 10;
      const canRedeem = task?.can_redeem ?? pendingCount >= redeemThreshold;
      return {
        ...previous,
        task,
        overview: previous.overview
          ? {
              ...previous.overview,
              current_task: task,
              pending_count: pendingCount,
              redeem_threshold: redeemThreshold,
              can_redeem: canRedeem,
            }
          : previous.overview,
        redeemRequired: canRedeem,
      };
    });
  }, []);

  const loadOverview = useCallback(async () => {
    setState((previous) => ({ ...previous, loading: true, error: '' }));
    try {
      const response = await h5ManualActivityApi.getOverview();
      const overview = response.data.data;
      setState((previous) => ({
        ...previous,
        loading: false,
        overview,
        task: overview.current_task,
        redemption: overview.active_redemption,
        redemptionPhone: overview.active_redemption?.whatsapp_phone_number || previous.redemptionPhone,
        redemptionAuthMethod: overview.active_redemption?.auth_method || previous.redemptionAuthMethod,
        credentialLoading: previous.credentialLoading
          && overview.active_redemption?.status === 'waiting_login'
          && !overview.active_redemption?.error_message
          && !hasLoginCredential(
            overview.active_redemption,
            overview.active_redemption?.auth_method || previous.redemptionAuthMethod
          ),
        redeemRequired: overview.can_redeem,
      }));
    } catch (error: any) {
      setState((previous) => ({
        ...previous,
        loading: false,
        error: error?.response?.data?.error?.message || t('loadFailed'),
      }));
    }
  }, [t]);

  const requestNextTask = useCallback(async () => {
    const expectsCredential = Boolean(
      state.redemption
        && state.redemption.status === 'waiting_login'
        && !hasLoginCredential(state.redemption, state.redemptionAuthMethod)
    );
    setState((previous) => ({
      ...previous,
      actionLoading: true,
      credentialLoading: expectsCredential,
      error: '',
    }));
    try {
      const response = await h5ManualActivityApi.getNextTask();
      applyTask(response.data.data);
    } catch (error: any) {
      const code = error?.response?.data?.error?.code;
      const message = error?.response?.data?.error?.message || t('claimFailed');
      if (code === 'REDEMPTION_REQUIRED') {
        setState((previous) => ({ ...previous, redeemRequired: true, error: '' }));
        Toast.show({ icon: 'fail', content: t('redeemFirst') });
      } else {
        setState((previous) => ({ ...previous, error: message }));
        Toast.show({ icon: 'fail', content: message });
      }
    } finally {
      setState((previous) => ({ ...previous, actionLoading: false, loading: false }));
    }
  }, [applyTask, t]);

  const copyText = useCallback(async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      Toast.show({ icon: 'success', content: t('copySuccess', { label }) });
    } catch {
      Toast.show({ icon: 'fail', content: t('copyFailed', { label }) });
    }
  }, [t]);

  const copyPhone = useCallback(async () => {
    if (!state.task) {
      return;
    }
    await copyText(state.task.target_phone, t('phoneLabel'));
  }, [copyText, state.task, t]);

  const copyMessage = useCallback(async () => {
    if (!state.task) {
      return;
    }
    await copyText(state.task.message_text, t('messageLabel'));
    try {
      const response = await h5ManualActivityApi.markCopied(state.task.id);
      applyTask(response.data.data);
    } catch {
      // The clipboard action already succeeded; keep the current task visible.
    }
  }, [applyTask, copyText, state.task, t]);

  const markSent = useCallback(async () => {
    if (!state.task) {
      return;
    }
    setState((previous) => ({ ...previous, actionLoading: true, error: '' }));
    try {
      const response = await h5ManualActivityApi.markClaimedSent(state.task.id);
      const task = response.data.data;
      const canRedeem = task.can_redeem === true;
      setState((previous) => ({
        ...previous,
        task: null,
        overview: previous.overview
          ? {
              ...previous.overview,
              current_task: null,
              pending_count: task.pending_count ?? previous.overview.pending_count,
              redeem_threshold: task.redeem_threshold ?? previous.overview.redeem_threshold,
              can_redeem: canRedeem,
            }
          : previous.overview,
        redeemRequired: canRedeem,
      }));
      Toast.show({ icon: 'success', content: canRedeem ? t('readyToRedeem') : t('addedPending') });
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || t('updateFailed');
      setState((previous) => ({ ...previous, error: message }));
      Toast.show({ icon: 'fail', content: message });
    } finally {
      setState((previous) => ({ ...previous, actionLoading: false }));
    }
  }, [state.task, t]);

  const startRedemption = useCallback(async () => {
    const phoneNumber = (state.redemptionPhone || state.redemption?.whatsapp_phone_number || '').trim();
    const needsLoginStart = !state.redemption
      || isCompletedRedemption(state.redemption)
      || (
        state.redemption.status === 'waiting_login'
        && !state.redemption.wa_connection_attempt_id
        && !state.redemption.wa_account_id
      );
    const needsRetry = isRetryableRedemption(state.redemption);
    const needsScanRetry = needsAccountScanRetry(state.redemption);
    if ((needsLoginStart || (needsRetry && !needsScanRetry)) && !phoneNumber) {
      Toast.show({ icon: 'fail', content: t('phoneRequired') });
      return;
    }
    setState((previous) => ({ ...previous, actionLoading: true, error: '' }));
    try {
      const response = needsScanRetry
        ? await h5ManualActivityApi.retryRedemptionSession(state.redemption!.id)
        : needsRetry
        ? await h5ManualActivityApi.switchRedemptionAuthMethod(state.redemption!.id, {
            phone_number: phoneNumber,
            auth_method: state.redemptionAuthMethod,
          })
        : needsLoginStart
        ? await h5ManualActivityApi.createRedemptionSession({
            phone_number: phoneNumber,
            auth_method: state.redemptionAuthMethod,
          })
        : await h5ManualActivityApi.getRedemptionSession(state.redemption!.id);
      const redemption = response.data.data;
      applyRedemption(redemption);
      Toast.show({ icon: 'success', content: t('redemptionStarted') });
    } catch (error: any) {
      const code = error?.response?.data?.error?.code;
      const message = error?.response?.data?.error?.message || t('redemptionStartFailed');

      // Backend guards redemption creation with a hard threshold check. When the
      // check fails the UI state is stale — refresh overview so the redeem card
      // hides itself and surface a friendly "N/M" hint pulled from the fresh data.
      if (code === 'H5_REDEMPTION_NOT_READY') {
        try {
          const response = await h5ManualActivityApi.getOverview();
          const overview = response.data.data;
          const task = overview.current_task;
          const pendingCount = Number(
            task?.pending_count ?? overview.pending_count ?? 0,
          );
          const threshold = Number(
            task?.redeem_threshold ?? overview.redeem_threshold ?? 10,
          );
          setState((previous) => ({
            ...previous,
            overview,
            task,
            redeemRequired: Boolean(overview.can_redeem),
            actionLoading: false,
            credentialLoading: false,
            error: '',
          }));
          Toast.show({
            icon: 'fail',
            content: t('redeemNotReady', { count: pendingCount, threshold }),
          });
          return;
        } catch {
          // Fall through to the generic branch if the refresh itself fails.
        }
      }

      setState((previous) => ({ ...previous, credentialLoading: false, error: message }));
      Toast.show({ icon: 'fail', content: message });
    } finally {
      setState((previous) => ({ ...previous, actionLoading: false }));
    }
  }, [applyRedemption, state.redemption, state.redemptionAuthMethod, state.redemptionPhone, t]);

  const switchRedemptionAuthMethod = useCallback(async (authMethod: H5RedemptionAuthMethod) => {
    const redemption = state.redemption;
    if (needsAccountScanRetry(redemption)) {
      setState((previous) => ({ ...previous, actionLoading: true, error: '' }));
      try {
        const response = await h5ManualActivityApi.retryRedemptionSession(redemption!.id);
        applyRedemption(response.data.data);
        Toast.show({ icon: 'success', content: t('redemptionStarted') });
      } catch (error: any) {
        const rawMessage = error?.response?.data?.error?.message || t('redemptionStartFailed');
        const message = isRuntimeBusyMessage(rawMessage)
          ? t('redemptionRuntimeBusyHint')
          : rawMessage;
        setState((previous) => ({ ...previous, credentialLoading: false, error: message }));
        Toast.show({ icon: 'fail', content: message });
      } finally {
        setState((previous) => ({ ...previous, actionLoading: false }));
      }
      return;
    }
    const retryingFailedCurrentMethod = Boolean(
      redemption
        && redemption.auth_method === authMethod
        && (
          ['failed', 'expired'].includes(redemption.status)
          || needsLoginCredentialRegeneration(redemption, authMethod)
        )
    );
    if (state.redemptionAuthMethod === authMethod && !retryingFailedCurrentMethod) {
      return;
    }
    const canSwitchActiveRedemption = Boolean(
      redemption
        && !redemption.wa_account_id
        && ['waiting_login', 'failed', 'expired'].includes(redemption.status)
    );
    if (!redemption || !canSwitchActiveRedemption) {
      setState((previous) => ({ ...previous, redemptionAuthMethod: authMethod }));
      return;
    }

    const phoneNumber = (state.redemptionPhone || redemption.whatsapp_phone_number || '').trim();
    if (!phoneNumber) {
      Toast.show({ icon: 'fail', content: t('phoneRequired') });
      return;
    }

    setState((previous) => ({
      ...previous,
      actionLoading: true,
      credentialLoading: true,
      redemptionAuthMethod: authMethod,
      redemptionQrImage: null,
      redemption: previous.redemption
        ? {
            ...previous.redemption,
            auth_method: authMethod,
            qr_payload: null,
            pairing_code: null,
            qr_expires_at: null,
            error_message: null,
          }
        : previous.redemption,
      error: '',
    }));
    try {
      const response = await h5ManualActivityApi.switchRedemptionAuthMethod(redemption.id, {
        phone_number: phoneNumber,
        auth_method: authMethod,
      });
      applyRedemption(response.data.data);
      Toast.show({ icon: 'success', content: t('authMethodSwitched') });
    } catch (error: any) {
      const rawMessage = error?.response?.data?.error?.message || t('authMethodSwitchFailed');
      const message = isRuntimeBusyMessage(rawMessage)
        ? t('redemptionRuntimeBusyHint')
        : rawMessage;
      setState((previous) => ({
        ...previous,
        redemptionAuthMethod: redemption.auth_method || previous.redemptionAuthMethod,
        credentialLoading: false,
        error: message,
      }));
      Toast.show({ icon: 'fail', content: message });
    } finally {
      setState((previous) => ({ ...previous, actionLoading: false }));
    }
  }, [
    applyRedemption,
    state.redemption,
    state.redemptionAuthMethod,
    state.redemptionPhone,
    t,
  ]);

  const regenerateRedemptionCredential = useCallback(async () => {
    const redemption = state.redemption;
    if (!redemption || redemption.wa_account_id || !['waiting_login', 'failed', 'expired'].includes(redemption.status)) {
      return;
    }
    const authMethod = state.redemptionAuthMethod || redemption.auth_method || 'pairing_code';
    const phoneNumber = (state.redemptionPhone || redemption.whatsapp_phone_number || '').trim();
    if (!phoneNumber) {
      Toast.show({ icon: 'fail', content: t('phoneRequired') });
      return;
    }

    setState((previous) => ({
      ...previous,
      actionLoading: true,
      credentialLoading: true,
      redemptionAuthMethod: authMethod,
      redemptionQrImage: null,
      redemption: previous.redemption
        ? {
            ...previous.redemption,
            auth_method: authMethod,
            wa_connection_attempt_id: null,
            qr_payload: null,
            pairing_code: null,
            qr_expires_at: null,
            error_message: null,
          }
        : previous.redemption,
      error: '',
    }));

    try {
      const response = await h5ManualActivityApi.switchRedemptionAuthMethod(redemption.id, {
        phone_number: phoneNumber,
        auth_method: authMethod,
        force_regenerate: true,
      });
      applyRedemption(response.data.data);
      Toast.show({ icon: 'success', content: t('authMethodSwitched') });
    } catch (error: any) {
      const rawMessage = error?.response?.data?.error?.message || t('authMethodSwitchFailed');
      const message = isRuntimeBusyMessage(rawMessage)
        ? t('redemptionRuntimeBusyHint')
        : rawMessage;
      setState((previous) => ({
        ...previous,
        credentialLoading: false,
        error: message,
      }));
      Toast.show({ icon: 'fail', content: message });
    } finally {
      setState((previous) => ({ ...previous, actionLoading: false }));
    }
  }, [
    applyRedemption,
    state.redemption,
    state.redemptionAuthMethod,
    state.redemptionPhone,
    t,
  ]);

  const refreshActiveRedemption = useCallback(async () => {
    const redemptionId = state.redemption?.id;
    if (!redemptionId) {
      await loadOverview();
      return;
    }
    try {
      const response = await h5ManualActivityApi.getRedemptionSession(redemptionId);
      applyRedemption(response.data.data);
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || t('loadFailed');
      setState((previous) => ({ ...previous, credentialLoading: false, error: message }));
    }
  }, [applyRedemption, loadOverview, state.redemption?.id, t]);

  const setRedemptionPhone = useCallback((phone: string) => {
    setState((previous) => ({ ...previous, redemptionPhone: phone }));
  }, []);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    let cancelled = false;
    const qrPayload = state.redemption?.qr_payload;
    if (!qrPayload) {
      setState((previous) => previous.redemptionQrImage ? { ...previous, redemptionQrImage: null } : previous);
      return;
    }
    QRCode.toDataURL(qrPayload, { width: 240, margin: 2 })
      .then((image) => {
        if (!cancelled) {
          setState((previous) => ({ ...previous, redemptionQrImage: image }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState((previous) => ({ ...previous, redemptionQrImage: null }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [state.redemption?.qr_payload]);

  useEffect(() => {
    const redemption = state.redemption;
    if (!redemption || !['waiting_login', 'logged_in', 'scanning'].includes(redemption.status)) {
      return;
    }
    const timer = window.setTimeout(() => {
      void refreshActiveRedemption();
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [refreshActiveRedemption, state.redemption?.id, state.redemption?.status, state.redemption?.updated_at]);

  return {
    state,
    loadOverview,
    requestNextTask,
    copyPhone,
    copyMessage,
    markSent,
    startRedemption,
    setRedemptionPhone,
    switchRedemptionAuthMethod,
    regenerateRedemptionCredential,
  };
}

export function getRedemptionCredentialDisplayError(
  message: string | null | undefined,
  t: (key: string) => string
): string | null {
  if (!message) {
    return null;
  }
  return isRuntimeBusyMessage(message) ? t('redemptionRuntimeBusyHint') : message;
}
